import {
  ContactPointOwnership,
  GuardianRelationship,
  NationalIdOwner,
} from "elephant-web/gql";
import {
  type ExtractDocumentTypeFromTypedRxJsonSchema,
  type RxCollection,
  type RxJsonSchema,
  toTypedRxJsonSchema,
} from "rxdb";
import { useRxCollection, useRxData } from "rxdb-hooks";
import { SYNCABLE_META } from "./common";

export const RXDB_PATIENT_COLLECTION_NAME = "patient";

// Type-safe wrappers for all rxdb-hooks hooks. These should be copy-pasted for each
// collection, lest a type-wizard arrives.
// https://github.com/cvara/rxdb-hooks?tab=readme-ov-file#userxdocument

/**
 * Note: when performing updates on RxDocuments, use the `incrementalX` functions
 * instead of their non-incremental counterparts as this helps to avoid local conflicts
 * https://rxdb.info/transactions-conflicts-revisions.html#local-conflicts
 *
 * Can be null if the collection is not ready yet - inbuilt types don't reveal
 * this.
 */
export const useRxCollectionPatient = (): RxCollection<Patient> | null =>
  useRxCollection<Patient>(RXDB_PATIENT_COLLECTION_NAME);
type UseRxData = Parameters<typeof useRxData<Patient>>;
export type PatientQueryConstructor = UseRxData["1"];
/**
 * @deprecated Use `useRxData<Patient>(RXDB_PATIENT_COLLECTION_NAME,
 * queryConstructor, options)` instead because this abstraction loses the type
 * inference given to you by the return type of the queryConstructor
 */
export const useRxDataPatient = (
  queryConstructor: UseRxData["1"],
  options?: UseRxData["2"],
) =>
  useRxData<Patient>(RXDB_PATIENT_COLLECTION_NAME, queryConstructor, options);

const PATIENT_SCHEMA_LITERAL = {
  title: "patient schema",
  version: 20,
  description: "A patient",
  type: "object",
  primaryKey: "id",
  properties: {
    address: {
      type: "object",
      properties: {
        country: {
          type: "string",
        },
        line1: {
          type: "string",
        },
        line2: {
          type: "string",
        },
        line3: {
          type: ["string", "null"],
        },
        line4: {
          type: "string",
        },
      },
      required: ["country", "line1", "line2", "line3", "line4"],
    },
    consent: {
      type: "object",
      properties: {
        elephantUseOfAnonData: {
          type: "boolean",
        },
        productMarketing: {
          type: "boolean",
        },
        territoryUseOfData: {
          type: "boolean",
        },
        accessToHealthRecords: {
          type: "boolean",
        },
        healthcareComms: {
          type: "boolean",
        },
      },
      required: [
        "elephantUseOfAnonData",
        "productMarketing",
        "territoryUseOfData",
        "accessToHealthRecords",
        "healthcareComms",
      ],
    },
    dob: {
      type: "string",
      format: "date",
    },
    education: {
      type: ["object", "null"],
      properties: {
        isStudent: {
          type: "boolean",
        },
        school: {
          type: ["string", "null"],
        },
        nemisNumber: {
          type: ["string", "null"],
        },
      },
      required: ["isStudent", "school", "nemisNumber"],
    },
    email: {
      type: ["string", "null"],
      format: "email",
    },
    facilityId: {
      type: "string",
    },
    gender: {
      type: "string",
      enum: ["m", "f", "transgender"],
    },
    id: {
      type: "string",
      maxLength: 100,
    },
    insurance: {
      type: ["object", "null"],
      properties: {
        type: {
          type: "string",
        },
        number: {
          type: ["string", "null"],
        },
        details: {
          type: ["object", "null"],
        },
      },
      required: ["type", "number", "details"],
    },
    maritalStatus: {
      type: "string",
      enum: ["divorced", "married", "single", "widowed", "unknown"],
    },
    meta: SYNCABLE_META,
    name: {
      type: "object",
      properties: {
        first: {
          type: "string",
        },
        middle: {
          type: ["string", "null"],
        },
        last: {
          type: "string",
        },
      },
      required: ["first", "last", "middle"],
    },
    nationalId: {
      type: ["object", "null"],
      properties: {
        value: {
          type: ["string", "null"],
        },
        owner: {
          oneOf: [
            {
              type: "null",
            },
            {
              type: "string",
              enum: Object.values(NationalIdOwner),
            },
          ],
        },
      },
      required: ["value", "owner"],
    },
    nationality: {
      type: "string",
    },
    newToFacility: {
      type: "boolean",
    },
    nextOfKin: {
      type: ["object", "null"],
      properties: {
        fullName: {
          type: "string",
        },
        relationship: {
          type: ["string", "null"],
          // TODO: Since adding validation it seems these are not the right values. Something for later.
          // enum: [ "husband", "wife", "brother", "sister", "father", "mother", "child", "friend", "other", ],
        },
        address: {
          type: ["object", "null"],
          properties: {
            country: {
              type: ["string", "null"],
            },
            line1: {
              type: ["string", "null"],
            },
            line2: {
              type: ["string", "null"],
            },
            line3: {
              type: ["string", "null"],
            },
            line4: {
              type: ["string", "null"],
            },
          },
          required: ["country", "line1", "line2", "line3", "line4"],
        },
        phoneNumber: {
          type: ["string", "null"],
        },
      },
      required: ["fullName", "relationship", "address", "phoneNumber"],
    },
    organisationId: {
      type: "string",
    },
    phone: {
      type: ["object", "null"],
      properties: {
        number: {
          type: "string",
        },
        ownership: {
          type: "string",
          enum: Object.values(ContactPointOwnership),
        },
      },
      required: ["number", "ownership"],
    },
    registrationFee: {
      description:
        "@deprecated: Is not a part of the Patient entity and must be removed",
      type: ["object", "null"],
      properties: {
        paidOn: {
          type: "string",
          format: "date",
        },
        receiptNumber: {
          type: ["string", "null"],
        },
      },
      required: ["paidOn", "receiptNumber"],
    },
    guardians: {
      type: ["array", "null"],
      items: {
        type: "object",
        properties: {
          id: {
            type: "string",
          },
          patientId: {
            type: ["string", "null"],
          },
          fullName: {
            type: ["string", "null"],
          },
          phone: {
            type: ["object", "null"],
            properties: {
              number: {
                type: "string",
              },
              ownership: {
                type: "string",
                enum: Object.values(ContactPointOwnership),
              },
            },
            required: ["number", "ownership"],
          },
          relationship: {
            type: "string",
            enum: Object.values(GuardianRelationship),
          },
          consents: {
            type: "object",
            properties: {
              healthcareComms: {
                type: "object",
                properties: {
                  value: {
                    type: "boolean",
                  },
                  providedAt: {
                    type: "string",
                    format: "date-time",
                  },
                },
                required: ["value", "providedAt"],
              },
              marketingComms: {
                type: "object",
                properties: {
                  value: {
                    type: "boolean",
                  },
                  providedAt: {
                    type: "string",
                    format: "date-time",
                  },
                },
                required: ["value", "providedAt"],
              },
            },
            required: ["healthcareComms", "marketingComms"],
          },
          // Annoyingly SYNCABLE_META does not yet have the deleted field, and
          // adding it would require doing a migration for every collection.
          // This is a temporary solution until we do that migration for every collection.
          meta: {
            type: "object",
            properties: {
              ...SYNCABLE_META.properties,
              deleted: {
                type: "boolean",
              },
            },
            required: [
              "createdAt",
              "createdByUserId",
              "updatedAt",
              "updatedByUserId",
              "deleted",
            ],
          },
        },
        required: ["id", "consents", "meta"],
      },
    },
  },
  required: [
    "address",
    "consent",
    "dob",
    "education",
    "email",
    "facilityId",
    "gender",
    "id",
    "insurance",
    "maritalStatus",
    "meta",
    "name",
    "nationalId",
    "nationality",
    "newToFacility",
    "nextOfKin",
    "organisationId",
    "phone",
    "registrationFee",
    "guardians",
  ],
  // https://elephant.slab.com/posts/offline-schema-fields-uwb0pbii#hlqhz-rxdb-dexie-elephant-7-patient
  encrypted: [
    "address",
    "consent",
    "dob",
    "education",
    "email",
    "facilityId",
    "gender",
    "insurance",
    "maritalStatus",
    "meta",
    "name",
    "nationalId",
    "nationality",
    "newToFacility",
    "nextOfKin",
    "organisationId",
    "phone",
    "registrationFee",
    "guardians",
  ],

  /*
   * It is important to set 'as const' to preserve the literal type.
   *
   * biome-ignore lint/suspicious/noExplicitAny: We infer the DB type from this
   * object, the satisfies just provides us type inference for this object.
   */
} as const satisfies RxJsonSchema<any>;

const schemaTyped = toTypedRxJsonSchema(PATIENT_SCHEMA_LITERAL);

// aggregate the document type from the schema
export type Patient = ExtractDocumentTypeFromTypedRxJsonSchema<
  typeof schemaTyped
>;

// create the typed RxJsonSchema from the literal typed object.
export const PATIENT_SCHEMA: RxJsonSchema<Patient> = PATIENT_SCHEMA_LITERAL;
